import * as MainModule from 'MainModule';
import * as Routing from './Aspect/Navigation/Routing';
import {getBrowser, handleBrowser} from './Aspect/Browser/Browser';
import {handleOpenNewTab} from './Aspect/Browser/OpenNewTab';
import {apply, debounce, getOS, whenReady} from './Aspect/util';
import {defineEventfullElement} from './UI/Eventfull/Eventfull';
import {AllPorts, ElmFrameworkPorts, OutPortsKeys} from './ElmFrameworkPorts';
import {AppPorts} from '../elm-ports';
import {handleDomPorts} from "./Aspect/Dom/dom-ports";
import {handleAuth} from "./Aspect/Auth/Auth";
import { defineCustomTooltip } from "./UI/CustomTooltip/CustomTooltip";
import * as LocalStorage from './Aspect/LocalStorage';
import {handleClipboard} from "./Aspect/Clipboard/Clipboard";



export {
  initKernel,
  InitConfig,
}


interface InitConfig {
  elSelector: string
  initArgs: object
  portHandlers: ((ports: AllPorts) => void)[]
  portHandlersOnReady: ((ports: AllPorts) => void)[]
}

function initKernel(config: InitConfig): void {
  document.fonts.ready.then(initTheKernel, initTheKernel);

  function initTheKernel(): void {
      defineWebComponents();
      const app: { ports: ElmFrameworkPorts & AppPorts } = failsafeElmInit(config);
      handleClipboard(app.ports);
      handleOpenNewTab(app.ports);
      handleErrors(config, app.ports);
      Routing.handleNavigation(app.ports);
      config.portHandlers.forEach(apply(app.ports));
      handleAuth(true, app.ports);
      handleTitleDescriptionUpdates(app.ports);
      whenReady.then(() => {
        handleBrowser(app.ports);
        app.ports.windowResize.send(getBrowser()); //because unitSize needs onload
        hideWebSplash();
        handleLifeCycle(app.ports);
        handleNetworkConnectivity(app.ports);
        config.portHandlersOnReady.forEach(apply(app.ports));
        handleDomPorts(app.ports);
      });
  }
}


/////////////////////////////////////////////////////////////////////////

function failsafeElmInit(config: InitConfig) {
  try {
    return MainModule.Elm.MainModule.init(elmProgramArgs(config.elSelector, config.initArgs));
  } catch (e) {
    LocalStorage.clear();
    return MainModule.Elm.MainModule.init(elmProgramArgs(config.elSelector, config.initArgs));
  }
}

function defineWebComponents(): void {
  defineEventfullElement();
  defineCustomTooltip();
}

function handleNetworkConnectivity(appPorts: ElmFrameworkPorts): void {
  window.addEventListener('online', () => {
    appPorts.weAreOnline.send(null);
  });
}

function handleLifeCycle(appPorts: ElmFrameworkPorts): void {
  const debouncedResume = debounce(() => appPorts.lifeCycleEvent.send('Resume'), 500);
  const debouncedPause = debounce(() => appPorts.lifeCycleEvent.send('Pause'), 500);
  window.addEventListener('focus', e => {
    if (e.target === window) {
      debouncedResume();
    }
  }, true);
  window.addEventListener('blur', e => {
    if (e.target === window) {
      debouncedPause();
    }
  }, true);
  document.addEventListener('visibilitychange', e => {
    if (document.hidden) {
      debouncedPause();
    } else {
      debouncedResume();
    }
  }, true);
}

function elmProgramArgs(elSelector: string, appArgs: object) {
  const osFamily = getOS();
  return {
    node: document.querySelector(elSelector),
    flags: {
      appArgs,
      kernelArgs: {
        env: Object.assign((process as any).env, {
          isProd: (process as any).env.isProd,
          isDev: (process as any).env.isDev,
          isStaging: (process as any).env.isStaging,
          isActiveDevelopment: (process as any).env.isActiveDevelopment,
        }),
        configArgs: (process as any).env.config,
        browser: getBrowser(),
        timezoneOffset: -(new Date().getTimezoneOffset()),
        currTime: (new Date).getTime(),
        navigationLocation: Routing.toLocation(),
      }
    }
  };
}

function handleErrors<SetUsrCtxPort extends OutPortsKeys>(config: InitConfig, ports: AllPorts): void {
  ports.detailedError?.subscribe(({msg}) => {
    // ErrorReporter.captureException(new Error(msg));
  }); // extra in detailedError needs to be stringified if we want it in the message
  ports.generalError?.subscribe(err => {
    // ErrorReporter.captureException(new Error(err));
  });
}

// function failsafeStart() {
//   try {
//     bootstrap()
//   } catch (ex) {
//     ErrorReporter.captureException(ex, {extra: {context: 'failsafe start'}});
//     clearPersistence();
//     bootstrap();
//     // location.reload(true);
//   }
// }


function hideWebSplash(): void {
  const splash = document.getElementById('splash');
  if (splash) {
    splash.className = 'leaving';
    ['transitionend',
      'webkitTransitionEnd',
      'oTransitionEnd',
      'otransitionend'
    ].forEach(e => splash.addEventListener(e, (_: any) => splash.remove()));
  } else {
    console.error('splash element not found');
  }
}

function handleTitleDescriptionUpdates(appPorts: ElmFrameworkPorts): void {
  appPorts.setTitleAndDescription?.subscribe(setTitleAndDesc);
}

function setTitleAndDesc(config: { title: string, description: string }): void {
  document.title = config.title;
  document.querySelector('meta[name="description"]')?.setAttribute('content', config.description);
}
